import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { GroupZoomSession, Client } from '../types';
import { Video, Calendar, ExternalLink, Loader2, AlertCircle, CheckCircle, XCircle, Plus, Edit, Trash2 } from 'lucide-react';

// Helper to format datetime for datetime-local input (YYYY-MM-DDTHH:mm)
const toDateTimeLocal = (dateString: string): string => {
  const d = new Date(dateString);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

interface ZoomSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editSession?: GroupZoomSession | null;
  groupClients: Client[];
}

const ZoomSessionModal: React.FC<ZoomSessionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editSession,
  groupClients,
}) => {
  const [clientId, setClientId] = useState<string>(editSession?.client_id?.toString() ?? '');
  const [sessionDate, setSessionDate] = useState(editSession ? toDateTimeLocal(editSession.session_date) : '');
  const [zoomLink, setZoomLink] = useState(editSession?.zoom_link ?? '');
  const [meetingId, setMeetingId] = useState(editSession?.meeting_id ?? '');
  const [status, setStatus] = useState<'scheduled' | 'completed' | 'cancelled'>(editSession?.status ?? 'scheduled');
  const [notes, setNotes] = useState(editSession?.notes ?? '');
  const [createForAll, setCreateForAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!editSession;

  useEffect(() => {
    if (isOpen) {
      if (editSession) {
        setClientId((editSession.client_id ?? editSession.client?.id)?.toString() ?? '');
        setSessionDate(toDateTimeLocal(editSession.session_date));
        setZoomLink(editSession.zoom_link ?? '');
        setMeetingId(editSession.meeting_id ?? '');
        setStatus(editSession.status);
        setNotes(editSession.notes ?? '');
        setCreateForAll(false);
      } else {
        setClientId(groupClients.length === 1 ? groupClients[0].id.toString() : '');
        setSessionDate('');
        setZoomLink('');
        setMeetingId('');
        setStatus('scheduled');
        setNotes('');
        setCreateForAll(false);
      }
      setError(null);
    }
  }, [isOpen, editSession, groupClients]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const sessionDateIso = sessionDate ? new Date(sessionDate).toISOString() : '';

      if (isEdit) {
        await apiService.updateGroupZoomSession(editSession.id, {
          session_date: sessionDateIso,
          zoom_link: zoomLink.trim() || undefined,
          meeting_id: meetingId.trim() || undefined,
          status,
          notes: notes.trim() || undefined,
        });
      } else {
        if (createForAll && groupClients.length > 0) {
          for (const client of groupClients) {
            await apiService.createGroupZoomSession({
              client_id: client.id,
              session_date: sessionDateIso,
              zoom_link: zoomLink || undefined,
              meeting_id: meetingId || undefined,
              status,
              notes: notes || undefined,
            });
          }
        } else {
          if (!clientId && !createForAll) {
            setError('Please select a client');
            setIsLoading(false);
            return;
          }
          if (createForAll && groupClients.length === 0) {
            setError('No group clients found');
            setIsLoading(false);
            return;
          }
          await apiService.createGroupZoomSession({
            client_id: parseInt(clientId, 10),
            session_date: sessionDateIso,
            zoom_link: zoomLink || undefined,
            meeting_id: meetingId || undefined,
            status,
            notes: notes || undefined,
          });
        }
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save zoom session');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-10 pb-10">
      <div className="relative p-6 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {isEdit ? 'Edit Zoom Session' : 'Add Zoom Session'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isLoading}
            aria-label="Close modal"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
              <p className="ml-3 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isEdit && (
            <>
              {groupClients.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                  No group clients found. Add group clients from the Clients page first.
                </div>
              ) : (
                <>
                  <div>
                    <label htmlFor="client" className="block text-sm font-medium text-gray-700 mb-1">
                      Client *
                    </label>
                    <select
                      id="client"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      required={!createForAll}
                      disabled={isLoading || createForAll}
                      aria-label="Select client"
                    >
                      <option value="">Select a client</option>
                      {groupClients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.user?.first_name} {c.user?.last_name} ({c.user?.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  {groupClients.length > 1 && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="createForAll"
                        checked={createForAll}
                        onChange={(e) => setCreateForAll(e.target.checked)}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        disabled={isLoading}
                        aria-label="Create for all group clients"
                      />
                      <label htmlFor="createForAll" className="ml-2 text-sm text-gray-700">
                        Create for all group clients
                      </label>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          <div>
            <label htmlFor="sessionDate" className="block text-sm font-medium text-gray-700 mb-1">
              Date & Time *
            </label>
            <input
              type="datetime-local"
              id="sessionDate"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              required
              disabled={isLoading}
              aria-label="Session date and time"
            />
          </div>

          <div>
            <label htmlFor="zoomLink" className="block text-sm font-medium text-gray-700 mb-1">
              Zoom Link
            </label>
            <input
              type="url"
              id="zoomLink"
              value={zoomLink}
              onChange={(e) => setZoomLink(e.target.value)}
              placeholder="https://zoom.us/j/..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              disabled={isLoading}
              aria-label="Zoom meeting link"
            />
          </div>

          <div>
            <label htmlFor="meetingId" className="block text-sm font-medium text-gray-700 mb-1">
              Meeting ID
            </label>
            <input
              type="text"
              id="meetingId"
              value={meetingId}
              onChange={(e) => setMeetingId(e.target.value)}
              placeholder="e.g. 123 456 7890"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              disabled={isLoading}
              aria-label="Zoom meeting ID"
            />
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              value={status}
              onChange={(e) => setStatus(e.target.value as 'scheduled' | 'completed' | 'cancelled')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              disabled={isLoading}
              aria-label="Session status"
            >
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              rows={3}
              disabled={isLoading}
              aria-label="Session notes"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
              disabled={isLoading || (!isEdit && groupClients.length === 0)}
            >
              {isLoading ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Session'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const GroupZoomSessions: React.FC = () => {
  const { user } = useAuth();
  const [zoomSessions, setZoomSessions] = useState<GroupZoomSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupClients, setGroupClients] = useState<Client[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingSession, setEditingSession] = useState<GroupZoomSession | null>(null);

  const isTrainer = user?.role === 'trainer';

  const fetchZoomSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const sessions = await apiService.getGroupZoomSessions();
      const sorted = sessions.sort((a, b) =>
        new Date(b.session_date).getTime() - new Date(a.session_date).getTime()
      );
      setZoomSessions(sorted);
    } catch (err: any) {
      console.error('Error fetching zoom sessions:', err);
      setError(err.message || 'Failed to load zoom sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGroupClients = useCallback(async () => {
    if (!isTrainer) return;
    try {
      const response = await apiService.getClients(1, '', '');
      const clients = (response.results || []).filter((c: Client) => c.category === 'group');
      setGroupClients(clients);
    } catch (err) {
      console.error('Error fetching group clients:', err);
    }
  }, [isTrainer]);

  useEffect(() => {
    fetchZoomSessions();
  }, [fetchZoomSessions]);

  useEffect(() => {
    if (isTrainer) {
      fetchGroupClients();
    }
  }, [isTrainer, fetchGroupClients]);

  const handleAddSession = () => {
    setEditingSession(null);
    setShowModal(true);
  };

  const handleEditSession = (session: GroupZoomSession) => {
    setEditingSession(session);
    setShowModal(true);
  };

  const handleDeleteSession = async (session: GroupZoomSession) => {
    if (!window.confirm(`Delete this zoom session for ${formatDateTime(session.session_date)}?`)) return;
    try {
      await apiService.deleteGroupZoomSession(session.id);
      fetchZoomSessions();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete session');
    }
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setEditingSession(null);
    fetchZoomSessions();
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Calendar className="h-5 w-5 text-blue-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Scheduled';
    }
  };

  const isUpcoming = (session: GroupZoomSession) => {
    return session.status === 'scheduled' && new Date(session.session_date) > new Date();
  };

  const upcomingSessions = zoomSessions.filter(isUpcoming);
  const pastSessions = zoomSessions.filter(session => !isUpcoming(session));

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Group Zoom Sessions</h1>
          <p className="mt-2 text-gray-600">
            {isTrainer ? 'Manage group coaching Zoom sessions' : 'Monthly group coaching sessions'}
          </p>
        </div>
        {isTrainer && (
          <button
            type="button"
            onClick={handleAddSession}
            className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
            aria-label="Add zoom session"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Session
          </button>
        )}
      </div>

      {/* Upcoming Sessions */}
      {upcomingSessions.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Sessions</h2>
          <div className="space-y-4">
            {upcomingSessions.map((session) => (
              <div key={session.id} className="bg-white rounded-lg shadow-md border-2 border-blue-500 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      {getStatusIcon(session.status)}
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center flex-wrap gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {isTrainer && session.client ? `${session.client.user?.first_name} ${session.client.user?.last_name} - ` : ''}Group Session
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(session.status)}`}>
                          {getStatusLabel(session.status)}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-600">
                        <Calendar className="h-4 w-4 mr-2" />
                        <span>{formatDateTime(session.session_date)}</span>
                      </div>
                    </div>
                  </div>
                  {isTrainer && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditSession(session)}
                        className="p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-md"
                        aria-label="Edit session"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSession(session)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md"
                        aria-label="Delete session"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {session.zoom_link ? (
                  <a
                    href={session.zoom_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <Video className="h-4 w-4 mr-2" />
                    Join Zoom Meeting
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </a>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex">
                      <Video className="h-5 w-5 text-yellow-400" />
                      <div className="ml-3">
                        <p className="text-sm text-yellow-800">
                          <strong>Zoom link will be available soon.</strong> Your trainer will share the meeting link before the session.
                        </p>
                        {session.meeting_id && (
                          <p className="text-xs text-yellow-700 mt-1">
                            Meeting ID: {session.meeting_id}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {session.notes && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">{session.notes}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Past Sessions */}
      {pastSessions.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Past Sessions</h2>
          <div className="space-y-4">
            {pastSessions.map((session) => (
              <div key={session.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 opacity-75">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      {getStatusIcon(session.status)}
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center flex-wrap gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {isTrainer && session.client ? `${session.client.user?.first_name} ${session.client.user?.last_name} - ` : ''}Group Session
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(session.status)}`}>
                          {getStatusLabel(session.status)}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-600">
                        <Calendar className="h-4 w-4 mr-2" />
                        <span>{formatDateTime(session.session_date)}</span>
                      </div>
                    </div>
                  </div>
                  {isTrainer && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditSession(session)}
                        className="p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-md"
                        aria-label="Edit session"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSession(session)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md"
                        aria-label="Delete session"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {session.notes && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">{session.notes}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {zoomSessions.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Zoom Sessions Scheduled</h3>
          <p className="text-gray-600">
            {isTrainer
              ? 'Add a session to get started. Click "Add Session" above.'
              : 'Your trainer will schedule group sessions for you soon.'}
          </p>
        </div>
      )}

      {isTrainer && (
        <ZoomSessionModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingSession(null);
          }}
          onSuccess={handleModalSuccess}
          editSession={editingSession}
          groupClients={groupClients}
        />
      )}
    </div>
  );
};

export default GroupZoomSessions;

